import Papa from 'papaparse';
import {
  ServerCreateSurveyRequest,
  EnumsSurveyStatus,
  EnumsSurveyType,
  EnumsQuestionType,
  EnumsClusterType
} from '../myApi';
import { DateTime } from 'luxon';

export interface CSVSurveyRow {
  サーベイ名: string;
  広告開始日時: string;
  広告終了日時: string;
  接触期間: string;
  タイプ: string;
  設問テキスト: string;
  ランダム並び換え: string;
  設問種別: string;
  回答1テキスト: string;
  回答1固定: string;
  回答1排他: string;
  回答1ポジ: string;
  回答2テキスト: string;
  回答2固定: string;
  回答2排他: string;
  回答2ポジ: string;
  回答3テキスト: string;
  回答3固定: string;
  回答3排他: string;
  回答3ポジ: string;
  回答4テキスト: string;
  回答4固定: string;
  回答4排他: string;
  回答4ポジ: string;
  回答5テキスト: string;
  回答5固定: string;
  回答5排他: string;
  回答5ポジ: string;
  '接触・非接触(ON/OFF)': string;
  接触: string;
  非接触: string;
  'クラスタ(ON/OFF)': string;
  'マーケティング区分MTeen': string;
  'マーケティング区分M1': string;
  'マーケティング区分M2': string;
  'マーケティング区分M3': string;
  'マーケティング区分FTeen': string;
  'マーケティング区分F1': string;
  'マーケティング区分F2': string;
  'マーケティング区分F3': string;
  '10代区分M10s': string;
  '10代区分M20s': string;
  '10代区分M30s': string;
  '10代区分M40s': string;
  '10代区分M50s': string;
  '10代区分F10s': string;
  '10代区分F20s': string;
  '10代区分F30s': string;
  '10代区分F40s': string;
  '10代区分F50s': string;
  '男女区分男性': string;
  '男女区分女性': string;
  クラスタ区分: string;
  'キャリア(ON/OFF)': string;
  'キャリアdocomo': string;
  'キャリアau': string;
  'キャリアsoftbank': string;
  'キャリアその他': string;
  'デバイス(ON/OFF)': string;
  'デバイスPC': string;
  'デバイスTV': string;
  'デバイススマホ/タブレット': string;
  'デバイスその他': string;
  'OS(ON/OFF)': string;
  'OSiOS': string;
  'OSAndroid': string;
  'OSその他': string;
  調査カテゴリ: string;
  目標回答数: string;
}

export const parseCSVFile = (file: File): Promise<CSVSurveyRow[]> => {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      encoding: 'UTF-8',
      complete: (result) => {
        if (result.errors.length > 0) {
          reject(new Error(`CSV解析エラー: ${result.errors[0].message}`));
        } else {
          // ヘッダーから改行を削除してデータを正規化
          const normalizedData = result.data.map((row: any) => {
            const normalizedRow: any = {};

            // 各キーから改行文字を削除して新しいオブジェクトを作成
            Object.keys(row).forEach(key => {
              // キーから改行文字(\n, \r\n, \r)を削除
              const normalizedKey = key.replace(/[\r\n]+/g, '');
              normalizedRow[normalizedKey] = row[key];
            });

            return normalizedRow;
          });

          resolve(normalizedData as CSVSurveyRow[]);
        }
      },
      error: (error) => {
        reject(new Error(`ファイル読み込みエラー: ${error.message}`));
      }
    });
  });
};

const parseBoolean = (value: string): boolean => {
  if (!value) return false;
  const upperValue = value.toUpperCase();
  return upperValue === 'TRUE' || upperValue === 'ON' || value === '1';
};

const parseNumber = (value: string | undefined): number => {
  if (!value || value.trim() === '') {
    return 0;
  }
  // カンマを除去してから数値に変換
  const cleanValue = value.replace(/,/g, '');
  const parsed = parseInt(cleanValue, 10);
  return isNaN(parsed) ? 0 : parsed;
};

const parseLift = (value: string | undefined): string => {
  if (!value || value.trim() === '') {
    return '0';
  }
  // ON/OFFの場合は1/0に変換、それ以外は数値として解析
  if (value.toUpperCase() === 'ON') {
    return '1';
  } else if (value.toUpperCase() === 'OFF') {
    return '0';
  }
  // 数値の場合はそのまま文字列として返す
  const parsed = parseInt(value, 10);
  return isNaN(parsed) ? '0' : parsed.toString();
};

const parseQuestionType = (type: string): EnumsQuestionType => {
  if (type.includes('シングルアンサー') || type === 'SINGLE' || type === 'QUESTION_TYPE_SINGLE_ANSWER') {
    return EnumsQuestionType.QUESTION_TYPE_SINGLE_ANSWER;
  }
  return EnumsQuestionType.QUESTION_TYPE_MULTI_ANSWER;
};

const parseSurveyType = (type: string): EnumsSurveyType => {
  if (type === '事前調査' || type === 'PRE_AD' || type === 'SURVEY_TYPE_PRE_AD') {
    return EnumsSurveyType.SURVEY_TYPE_PRE_AD;
  }
  return EnumsSurveyType.SURVEY_TYPE_AD_CONTACT;
};

const parseClusterType = (type: string): EnumsClusterType => {
  if (type.includes('年代区分') || type.includes('10代区分') || type === 'CLUSTER_TYPE_TENS_CLUSTER') {
    return EnumsClusterType.CLUSTER_TYPE_TENS_CLUSTER;
  } else if (type.includes('性別') || type === 'CLUSTER_TYPE_GENDER') {
    return EnumsClusterType.CLUSTER_TYPE_GENDER;
  } else if (type.includes('マーケティング') || type === 'CLUSTER_TYPE_MARKET_CLUSTER') {
    return EnumsClusterType.CLUSTER_TYPE_MARKET_CLUSTER;
  }
  return EnumsClusterType.CLUSTER_TYPE_UNSPECIFIED;
};

const parseDateTime = (dateStr: string): string => {
  // 日本時間の日付文字列(YYYY/MM/DD HH:mm)をUnixタイムスタンプに変換
  const dt = DateTime.fromFormat(dateStr, 'yyyy/MM/dd H:mm:ss', { zone: 'Asia/Tokyo' });
  if (!dt.isValid) {
    throw new Error(`無効な日付形式: ${dateStr}`);
  }
  return dt.toSeconds().toString();
};

export const convertCSVToSurveyRequests = (csvData: CSVSurveyRow[]): ServerCreateSurveyRequest[] => {
  const surveyRequests: ServerCreateSurveyRequest[] = [];

  // CSVデータをグループ化(同じサーベイ名でまとめる)
  const groupedData = csvData.reduce((acc, row) => {
    const surveyName = row.サーベイ名;
    if (!acc[surveyName]) {
      acc[surveyName] = [];
    }
    acc[surveyName].push(row);
    return acc;
  }, {} as { [key: string]: CSVSurveyRow[] });

  // 各サーベイごとに変換
  Object.entries(groupedData).forEach(([surveyName, rows]) => {
    const firstRow = rows[0];

    // 複数質問の処理
    const questions = rows.map((row, index) => {
      return {
        order: index,
        type: parseQuestionType(row.タイプ),
        text: row.設問テキスト,
        random: parseBoolean(row.ランダム並び換え),
        imageUrl: '',
        kind: row.設問種別,
        answers: [
          {
            order: 0,
            text: row.回答1テキスト,
            fixOrder: parseBoolean(row.回答1固定),
            exclusive: parseBoolean(row.回答1排他),
            lift: parseLift(row.回答1ポジ)
          },
          {
            order: 1,
            text: row.回答2テキスト,
            fixOrder: parseBoolean(row.回答2固定),
            exclusive: parseBoolean(row.回答2排他),
            lift: parseLift(row.回答2ポジ)
          },
          {
            order: 2,
            text: row.回答3テキスト,
            fixOrder: parseBoolean(row.回答3固定),
            exclusive: parseBoolean(row.回答3排他),
            lift: parseLift(row.回答3ポジ)
          },
          {
            order: 3,
            text: row.回答4テキスト,
            fixOrder: parseBoolean(row.回答4固定),
            exclusive: parseBoolean(row.回答4排他),
            lift: parseLift(row.回答4ポジ)
          },
          {
            order: 4,
            text: row.回答5テキスト,
            fixOrder: parseBoolean(row.回答5固定),
            exclusive: parseBoolean(row.回答5排他),
            lift: parseLift(row.回答5ポジ)
          }
        ].filter(answer => answer.text) // 空の回答は除外
      };
    });

    const request: ServerCreateSurveyRequest = {
      name: surveyName,
      status: EnumsSurveyStatus.SURVEY_STATUS_ON,
      type: parseSurveyType(firstRow.調査カテゴリ),
      memo: '',
      surveyStartAt: parseDateTime(firstRow.広告開始日時),
      surveyEndAt: parseDateTime(firstRow.広告終了日時),
      targetCollectionNum: parseNumber(firstRow.目標回答数),

      // 接触期間設定
      recency: {
        isActive: true,
        days: parseNumber(firstRow.接触期間)
      },

      // リセット設定(デフォルト値)
      reset: {
        isActive: false
      },

      // 質問の設定
      questions: questions,

      // ターゲット設定
      targetRatio: {
        clusterType: parseClusterType(firstRow.クラスタ区分),

        contactRatio: {
          isActive: parseBoolean(firstRow['接触・非接触(ON/OFF)']),
          adContactStatusTrueRatio: parseNumber(firstRow.接触),
          adContactStatusFalseRatio: parseNumber(firstRow.非接触)
        },

        clusterRatio: {
          isActive: parseBoolean(firstRow['クラスタ(ON/OFF)']),
          clusterMarketMteenRatio: parseNumber(firstRow['マーケティング区分MTeen']),
          clusterMarketM1Ratio: parseNumber(firstRow['マーケティング区分M1']),
          clusterMarketM2Ratio: parseNumber(firstRow['マーケティング区分M2']),
          clusterMarketM3Ratio: parseNumber(firstRow['マーケティング区分M3']),
          clusterMarketFteenRatio: parseNumber(firstRow['マーケティング区分FTeen']),
          clusterMarketF1Ratio: parseNumber(firstRow['マーケティング区分F1']),
          clusterMarketF2Ratio: parseNumber(firstRow['マーケティング区分F2']),
          clusterMarketF3Ratio: parseNumber(firstRow['マーケティング区分F3']),
          clusterTensM10sRatio: parseNumber(firstRow['10代区分M10s']),
          clusterTensM20sRatio: parseNumber(firstRow['10代区分M20s']),
          clusterTensM30sRatio: parseNumber(firstRow['10代区分M30s']),
          clusterTensM40sRatio: parseNumber(firstRow['10代区分M40s']),
          clusterTensM50sRatio: parseNumber(firstRow['10代区分M50s']),
          clusterTensF10sRatio: parseNumber(firstRow['10代区分F10s']),
          clusterTensF20sRatio: parseNumber(firstRow['10代区分F20s']),
          clusterTensF30sRatio: parseNumber(firstRow['10代区分F30s']),
          clusterTensF40sRatio: parseNumber(firstRow['10代区分F40s']),
          clusterTensF50sRatio: parseNumber(firstRow['10代区分F50s']),
          clusterGenderMaleRatio: parseNumber(firstRow['男女区分男性']),
          clusterGenderFemaleRatio: parseNumber(firstRow['男女区分女性'])
        },

        carrerRatio: {
          isActive: parseBoolean(firstRow['キャリア(ON/OFF)']),
          careerDocomoRatio: parseNumber(firstRow['キャリアdocomo']),
          careerAuRatio: parseNumber(firstRow['キャリアau']),
          careerSoftbankRatio: parseNumber(firstRow['キャリアsoftbank']),
          careerOtherRatio: parseNumber(firstRow['キャリアその他'])
        },

        deviceRatio: {
          isActive: parseBoolean(firstRow['デバイス(ON/OFF)']),
          devicePcRatio: parseNumber(firstRow['デバイスPC']),
          deviceTvRatio: parseNumber(firstRow['デバイスTV']),
          deviceSpRatio: parseNumber(firstRow['デバイススマホ/タブレット']),
          deviceOtherRatio: parseNumber(firstRow['デバイスその他'])
        },

        osRatio: {
          isActive: parseBoolean(firstRow['OS(ON/OFF)']),
          osIosRatio: parseNumber(firstRow['OSiOS']),
          osAndroidRatio: parseNumber(firstRow['OSAndroid']),
          osOtherRatio: parseNumber(firstRow['OSその他'])
        }
      }
    };

    surveyRequests.push(request);
  });

  return surveyRequests;
};

export const validateSurveyData = (surveys: ServerCreateSurveyRequest[]): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];

  if (surveys.length === 0) {
    errors.push('アップロードするサーベイがありません。CSVファイルを確認してください。');
    return { isValid: false, errors };
  }

  surveys.forEach((survey, index) => {
    // 必須フィールドのチェック
    if (!survey.name) {
      errors.push(`サーベイ${index + 1}: サーベイ名が入力されていません`);
    }

    if (!survey.surveyStartAt || !survey.surveyEndAt) {
      errors.push(`サーベイ${index + 1}: 配信期間が設定されていません`);
    }

    // 期間の妥当性チェック
    if (survey.surveyStartAt && survey.surveyEndAt) {
      const start = parseInt(survey.surveyStartAt, 10);
      const end = parseInt(survey.surveyEndAt, 10);
      if (start >= end) {
        errors.push(`サーベイ${index + 1}: 開始日時は終了日時より前である必要があります`);
      }
    }

    if (!survey.targetCollectionNum || survey.targetCollectionNum <= 0) {
      errors.push(`サーベイ${index + 1}: 目標回答数が正しく設定されていません`);
    }

    // 質問のチェック
    if (!survey.questions || survey.questions.length === 0) {
      errors.push(`サーベイ${index + 1}: 質問が設定されていません`);
    } else {
      survey.questions.forEach((question, qIndex) => {
        if (!question.text) {
          errors.push(`サーベイ${index + 1}: 質問${qIndex + 1}のテキストが入力されていません`);
        }
        if (!question.answers || question.answers.length < 2) {
          errors.push(`サーベイ${index + 1}: 質問${qIndex + 1}の回答が2つ以上必要です`);
        }

        // 排他回答のチェック
        let exclusiveCount = 0;
        question.answers?.forEach((answer, aIndex) => {
          if (answer.exclusive) {
            exclusiveCount++;
          }
          if (!answer.text) {
            errors.push(`サーベイ${index + 1}: 質問${qIndex + 1}の回答${aIndex + 1}のテキストが入力されていません`);
          }
        });

        if (exclusiveCount > 1) {
          errors.push(`サーベイ${index + 1}: 質問${qIndex + 1}で複数の排他回答が設定されています。排他回答は1つまでです。`);
        }
      });
    }

    // 比率のチェック
    if (survey.targetRatio) {
      // 接触・非接触比率
      if (survey.targetRatio.contactRatio?.isActive) {
        const contactSum = (survey.targetRatio.contactRatio.adContactStatusTrueRatio || 0) +
          (survey.targetRatio.contactRatio.adContactStatusFalseRatio || 0);
        if (contactSum === 0) {
          errors.push(`サーベイ${index + 1}: 接触・非接触の比率が設定されていません`);
        }
      }

      // クラスタ比率のチェック
      if (survey.targetRatio.clusterRatio?.isActive) {
        const clusterType = survey.targetRatio.clusterType;

        if (clusterType === EnumsClusterType.CLUSTER_TYPE_MARKET_CLUSTER) {
          const marketSum = (survey.targetRatio.clusterRatio.clusterMarketMteenRatio || 0) +
            (survey.targetRatio.clusterRatio.clusterMarketM1Ratio || 0) +
            (survey.targetRatio.clusterRatio.clusterMarketM2Ratio || 0) +
            (survey.targetRatio.clusterRatio.clusterMarketM3Ratio || 0) +
            (survey.targetRatio.clusterRatio.clusterMarketFteenRatio || 0) +
            (survey.targetRatio.clusterRatio.clusterMarketF1Ratio || 0) +
            (survey.targetRatio.clusterRatio.clusterMarketF2Ratio || 0) +
            (survey.targetRatio.clusterRatio.clusterMarketF3Ratio || 0);
          if (marketSum === 0) {
            errors.push(`サーベイ${index + 1}: マーケティング区分の比率が設定されていません`);
          }
        } else if (clusterType === EnumsClusterType.CLUSTER_TYPE_TENS_CLUSTER) {
          const tensSum = (survey.targetRatio.clusterRatio.clusterTensM10sRatio || 0) +
            (survey.targetRatio.clusterRatio.clusterTensM20sRatio || 0) +
            (survey.targetRatio.clusterRatio.clusterTensM30sRatio || 0) +
            (survey.targetRatio.clusterRatio.clusterTensM40sRatio || 0) +
            (survey.targetRatio.clusterRatio.clusterTensM50sRatio || 0) +
            (survey.targetRatio.clusterRatio.clusterTensF10sRatio || 0) +
            (survey.targetRatio.clusterRatio.clusterTensF20sRatio || 0) +
            (survey.targetRatio.clusterRatio.clusterTensF30sRatio || 0) +
            (survey.targetRatio.clusterRatio.clusterTensF40sRatio || 0) +
            (survey.targetRatio.clusterRatio.clusterTensF50sRatio || 0);
          if (tensSum === 0) {
            errors.push(`サーベイ${index + 1}: 年代区分の比率が設定されていません`);
          }
        } else if (clusterType === EnumsClusterType.CLUSTER_TYPE_GENDER) {
          const genderSum = (survey.targetRatio.clusterRatio.clusterGenderMaleRatio || 0) +
            (survey.targetRatio.clusterRatio.clusterGenderFemaleRatio || 0);
          if (genderSum === 0) {
            errors.push(`サーベイ${index + 1}: 男女区分の比率が設定されていません`);
          }
        }
      }

      // デバイス比率
      if (survey.targetRatio.deviceRatio?.isActive) {
        const deviceSum = (survey.targetRatio.deviceRatio.devicePcRatio || 0) +
          (survey.targetRatio.deviceRatio.deviceTvRatio || 0) +
          (survey.targetRatio.deviceRatio.deviceSpRatio || 0) +
          (survey.targetRatio.deviceRatio.deviceOtherRatio || 0);
        if (deviceSum === 0) {
          errors.push(`サーベイ${index + 1}: デバイス比率が設定されていません`);
        }
      }

      // OS比率
      if (survey.targetRatio.osRatio?.isActive) {
        const osSum = (survey.targetRatio.osRatio.osIosRatio || 0) +
          (survey.targetRatio.osRatio.osAndroidRatio || 0) +
          (survey.targetRatio.osRatio.osOtherRatio || 0);
        if (osSum === 0) {
          errors.push(`サーベイ${index + 1}: OS比率が設定されていません`);
        }
      }

      // キャリア比率
      if (survey.targetRatio.carrerRatio?.isActive) {
        const carrerSum = (survey.targetRatio.carrerRatio.careerDocomoRatio || 0) +
          (survey.targetRatio.carrerRatio.careerAuRatio || 0) +
          (survey.targetRatio.carrerRatio.careerSoftbankRatio || 0) +
          (survey.targetRatio.carrerRatio.careerOtherRatio || 0);
        if (carrerSum === 0) {
          errors.push(`サーベイ${index + 1}: キャリア比率が設定されていません`);
        }
      }
    }
  });

  return {
    isValid: errors.length === 0,
    errors
  };
};